/**
 * Hotel Details Selectors
 *
 * Memoized selectors for accessing hotel details state.
 * Using createSelector ensures components only re-render when selected data changes.
 */
import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@/store/store';

// ============================================
// Base Selectors
// ============================================

const selectDetailsState = (state: RootState) => state.hotels.details;

// ============================================
// Hotel Details Selectors (Memoized)
// ============================================

export const selectHotelDetailsData = createSelector(
  [selectDetailsState],
  (details) => details.data,
);

export const selectHotelDetailsLoading = createSelector(
  [selectDetailsState],
  (details) => details.isLoading,
);

export const selectHotelDetailsError = createSelector(
  [selectDetailsState],
  (details) => details.error,
);

// Alias for selectHotelDetailsData (more intuitive name for the polling hook)
export const selectHotelDetails = selectHotelDetailsData;

export const selectIsLoadingStaticDetails = createSelector(
  [selectDetailsState],
  (details) => details.isLoadingStaticDetails,
);

// ============================================
// Hotel Details Flags Selectors (Memoized)
// ============================================

export const selectHotelDetailsFlags = createSelector(
  [selectDetailsState],
  (details) => details.flags,
);

export const selectIsGalleryOpen = createSelector(
  [selectDetailsState],
  (details) => details.flags.openGallery,
);

export const selectIsReviewsOpen = createSelector(
  [selectDetailsState],
  (details) => details.flags.openReviews,
);

// ============================================
// Hotel Basic Info Selectors (Memoized)
// ============================================

export const selectHotelName = createSelector(
  [selectHotelDetailsData],
  (data) => data?.header?.name ?? '',
);

export const selectHotelCityName = createSelector([selectHotelDetailsData], (data) => {
  // Extract city name from location string (e.g., "Ho Chi Minh City, Vietnam" -> "Ho Chi Minh City")
  const location = data?.header?.location ?? '';
  return location.split(',')[0]?.trim() ?? '';
});
